<template>
  <RecentActivitySkeleton v-if="loading" v-bind="$attrs" />

  <ul
    v-else
    v-bind="$attrs"
    class="flex flex-row gap-2 sm:gap-3 justify-between overflow-x-auto hide-scrollbar"
  >
    <li v-for="activity in recentActivities" :key="activity.date">
      <UserAvatarAndName
        :user-fragment="activity.user as UserAvatarAndNameType"
        :has-paid-membership="
          activity.user_is_superfan ??
          activity.user_has_paid_membership ??
          false
        "
        :badge-label="activity.points ?? 0"
        :badge-style="activity.has_multiplier_applied ? 'orange' : 'gold'"
        aspect="square"
      >
        <p class="text-xs text-text truncate w-full font-sans">
          {{ activity.location_label ?? activity.short_title }}
        </p>

        <p class="text-xs text-white/60">
          {{ activity.formattedDate }}
        </p>
      </UserAvatarAndName>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { graphql as gql } from "@/generated";
import { useChannelStore } from "@/stores/channel.store";
import { computed, watch } from "vue";
import UserAvatarAndName, {
  type UserAvatarAndName as UserAvatarAndNameType,
} from "./user-avatar-and-name.vue";
import { useUserStore } from "@/stores/user.store";
import { useQuery } from "@urql/vue";
import { useLoyaltyNotifications } from "../../features/channel/services/use-loyalty-notifications";
import { useTranslateObjectSimple } from "@/stores/translation.store";
import RecentActivitySkeleton from "@/components/ui/skeleton/recent-activity-skeleton.vue";

const { notifications } = useLoyaltyNotifications();

/**
 * We use this to regenerate the recent-activity-row when the user
 * performs an action that generates a loyalty notification.
 */
const lastNotificationId = computed(
  () => notifications[notifications.length - 1]?.id ?? null,
);

const ChannelRecentActivityDocument = gql(/* GraphQL */ `
  query ChannelRecentActivity(
    $channel_id: String!
    $limit: Float!
    $community_id: String
  ) {
    recentUserChannelActivities(
      channel_id: $channel_id
      limit: $limit
      community_id: $community_id
    ) {
      date
      name
      short_title
      app_action_translations {
        language
        short_title
      }
      points
      user_has_paid_membership
      user_is_superfan
      has_multiplier_applied
      location_label

      user {
        ...UserAvatarAndName
      }
    }
  }
`);

const channelStore = useChannelStore();
const userStore = useUserStore();

const {
  data,
  executeQuery: refetch,
  fetching,
} = useQuery({
  query: ChannelRecentActivityDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    limit: 12,
    community_id: channelStore.selectedCommunity ?? null,
    __last_notification_id: lastNotificationId.value,
  })),
  pause: computed(() => !channelStore.channelSlug),
});

const loading = computed(() => fetching.value || !channelStore.channel);

watch(
  () => channelStore.lastLeaderboardUpdate,
  (newDate, oldDate) => {
    if ((newDate && !oldDate) || (newDate && oldDate && newDate > oldDate)) {
      refetch();
    }
  },
);

const timeFormatter = computed(
  () =>
    new Intl.RelativeTimeFormat(userStore.userLocale, { numeric: "always" }),
);

const unitsInSecondsMap = {
  second: 60,
  minute: 3600,
  hour: 86400,
  day: 86400 * 7,
  week: 86400 * 30,
  month: 86400 * 365,
  year: Infinity,
};

function formatRelativeDate(date: Date) {
  // Calculate the difference in seconds between the given date and the current date
  const secondsDiff = Math.round((date.getTime() - Date.now()) / 1000);

  let unit: keyof typeof unitsInSecondsMap = "second";
  let divisor = 1;

  for (const [key, value] of Object.entries(unitsInSecondsMap)) {
    unit = key as keyof typeof unitsInSecondsMap;

    if (value > Math.abs(secondsDiff)) {
      break;
    } else {
      divisor = value;
    }
  }

  // Format the relative time based on the calculated unit
  return timeFormatter.value.format(Math.floor(secondsDiff / divisor), unit);
}

const { translateObject } = useTranslateObjectSimple();
const recentActivities = computed(() => {
  const list = (data.value?.recentUserChannelActivities ?? [])
    .filter((activity) => activity.points && activity.points > 0)
    .map((activity) => {
      const date = new Date(activity.date);
      const formattedDate = formatRelativeDate(date);
      const activityTranslations = translateObject<{ short_title: string }>({
        object: activity,
        objectTranslations: activity.app_action_translations,
        userLanguage: userStore.userLanguage,
      });

      return {
        ...activity,
        formattedDate,
        short_title: activityTranslations.short_title,
      };
    });

  list.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return list;
});
</script>
